import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

export function Modal({ isOpen, onClose, title, children }: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Close on click outside
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Use portal to render modal at document body level (fixes z-index issues)
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] overflow-y-auto"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.7)" }}
      onClick={handleBackdropClick}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      tabIndex={-1}
      data-testid="modal-overlay"
    >
      <div className="min-h-full flex items-center justify-center p-4">
        <div
          ref={modalRef}
          className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          style={{
            backgroundColor: "var(--color-surface)",
            border: "var(--neo-border-chunky)",
            boxShadow: "var(--neo-shadow-xl)",
          }}
          data-testid="modal-content"
        >
          {/* Header with close button */}
          <div
            className="flex items-center justify-between p-6 pb-4"
            style={{
              borderBottom: "var(--neo-border-medium)",
            }}
          >
            <h2
              id="modal-title"
              className="font-display text-3xl font-bold flex-1"
              style={{ color: "var(--color-text-primary)" }}
              data-testid="modal-title"
            >
              {title}
            </h2>
            <button
              onClick={onClose}
              className="flex-shrink-0 ml-4 font-bold"
              style={{
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "24px",
                color: "var(--color-surface)",
                backgroundColor: "var(--color-text-primary)",
                border: "var(--neo-border-thin)",
                cursor: "pointer",
                transition: "all 150ms ease-out",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translate(-2px, -2px)";
                e.currentTarget.style.boxShadow = "var(--neo-shadow-md)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translate(0, 0)";
                e.currentTarget.style.boxShadow = "none";
              }}
              aria-label="Close modal"
              data-testid="modal-close-button"
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div
            style={{ color: "var(--color-text-primary)" }}
            className="p-6 pt-4"
          >
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
